import React, { useState, useEffect, useCallback } from "react";
import { db } from "../firebase/firebaseConfig"; // ✅ Fixed import path
import {
  collection, addDoc, getDocs,
  query, orderBy, limit, serverTimestamp,
} from "firebase/firestore";

/* ─── Design tokens ───────────────────────────────────────── */
const T = {
  rose:      "#c0506e",
  roseDark:  "#8f2d46",
  roseLight: "#e8839a",
  roseSoft:  "#fdf0f3",
  roseMid:   "#f3c5cf",
  ivory:     "#faf8f5",
  ivoryDark: "#f0ece6",
  warm100:   "#e8e3dc",
  warm200:   "#d4cfc8",
  warm400:   "#9e9189",
  warm700:   "#3d3530",
  ink:       "#1a1510",
  ffDisplay: "'Cormorant Garamond', Georgia, serif",
  ffBody:    "'Outfit', system-ui, sans-serif",
};

/* ─── Star rating ─────────────────────────────────────────── */
function StarRating({ value, onChange, readOnly = false, size = 24 }) {
  const [hover, setHover] = useState(0);
  return (
    <div
      style={{ display: "flex", gap: 3, alignItems: "center" }}
      aria-label={readOnly ? `Rating: ${value} out of 5` : "Select rating"}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => !readOnly && onChange?.(n)}
          onMouseEnter={() => !readOnly && setHover(n)}
          onMouseLeave={() => !readOnly && setHover(0)}
          disabled={readOnly}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          style={{
            background: "none",
            border: "none",
            padding: "1px",
            cursor: readOnly ? "default" : "pointer",
            fontSize: size,
            color: (hover || value) >= n ? "#f59e0b" : T.warm200,
            transform: !readOnly && hover === n ? "scale(1.25)" : "scale(1)",
            transition: "color 120ms, transform 150ms",
            lineHeight: 1,
          }}
        >★</button>
      ))}
    </div>
  );
}

/* ─── Review card ─────────────────────────────────────────── */
function ReviewCard({ review, idx }) {
  return (
    <div style={{
      background: "#fff",
      border: `1px solid ${T.warm100}`,
      borderRadius: 14,
      padding: "1.25rem",
      animation: `fadeUp 360ms ${idx * 60}ms cubic-bezier(0.16,1,0.3,1) both`,
      transition: "box-shadow 250ms",
    }}
      onMouseEnter={(e) => e.currentTarget.style.boxShadow = `0 6px 24px rgba(0,0,0,.07)`}
      onMouseLeave={(e) => e.currentTarget.style.boxShadow = "none"}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{
          width: 40, height: 40,
          borderRadius: "50%",
          background: T.rose,
          color: "#fff",
          fontFamily: T.ffDisplay,
          fontWeight: 700,
          fontSize: 17,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }} aria-hidden="true">
          {review.name.charAt(0).toUpperCase()}
        </div>
        <div>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: T.ink, fontFamily: T.ffBody }}>
            {review.name}
          </p>
          <StarRating value={review.rating || 5} readOnly size={14} />
        </div>
      </div>
      <p style={{ margin: 0, fontSize: 14, color: T.warm400, lineHeight: 1.65, fontStyle: "italic", fontFamily: T.ffBody }}>
        "{review.review}"
      </p>
    </div>
  );
}

/* ─── Main component ──────────────────────────────────────── */
const ReviewForm = () => {
  const [form, setForm]       = useState({ name: "", review: "", rating: 5 });
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState("");
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  const set  = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const blur = (key) => () => setTouched((p) => ({ ...p, [key]: true }));

  const errors = {
    name:   touched.name   && !form.name.trim()            ? "Your name is required" : null,
    review: touched.review && form.review.trim().length < 10 ? "Review must be at least 10 characters" : null,
  };

  const canSubmit = form.name.trim() && form.review.trim().length >= 10 && !loading;

  const fetchReviews = useCallback(async () => {
    try {
      const q = query(collection(db, "reviews"), orderBy("timestamp", "desc"), limit(6));
      const snap = await getDocs(q);
      setReviews(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Failed to fetch reviews:", e);
    } finally {
      setLoadingReviews(false);
    }
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, review: true });
    setError("");

    if (!canSubmit) return;

    setLoading(true);
    try {
      await addDoc(collection(db, "reviews"), {
        name:      form.name.trim(),
        review:    form.review.trim(),
        rating:    form.rating,
        timestamp: serverTimestamp(),
      });
      setForm({ name: "", review: "", rating: 5 });
      setTouched({});
      setSuccess(true);
      await fetchReviews(); // Refresh list immediately
      setTimeout(() => setSuccess(false), 6000);
    } catch (err) {
      console.error("Review submit error:", err);
      setError("Failed to submit review. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600&family=Outfit:wght@400;500;600&display=swap');
        @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin   { to { transform: rotate(360deg); } }
        .rv-submit:not(:disabled):hover {
          background: ${T.roseDark} !important;
          box-shadow: 0 8px 28px rgba(192,80,110,.32) !important;
          transform: translateY(-2px);
        }
        .rv-submit { transition: background 220ms, box-shadow 220ms, transform 200ms; }
        .rv-submit:disabled { opacity: .5; cursor: not-allowed; }

        .rv-input {
          width: 100%;
          box-sizing: border-box;
          padding: 0.75rem 1rem;
          font-family: ${T.ffBody};
          font-size: 14px;
          color: ${T.ink};
          background: ${T.ivory};
          border: 1.5px solid ${T.warm200};
          border-radius: 10px;
          outline: none;
          transition: border-color 200ms, background 200ms, box-shadow 200ms;
        }
        .rv-input:focus {
          background: #fff;
          border-color: ${T.rose};
          box-shadow: 0 0 0 3px rgba(192,80,110,.12);
        }
        .rv-input::placeholder { color: ${T.warm400}; }

        .rv-reviews-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
          gap: 1rem;
          margin-top: 2rem;
        }
      `}</style>

      <div style={{
        minHeight: "100vh",
        background: `
          radial-gradient(ellipse 70% 50% at 50% 0%, rgba(192,80,110,.06) 0%, transparent 60%),
          ${T.ivoryDark}
        `,
        fontFamily: T.ffBody,
        padding: "clamp(2rem, 6vw, 4.5rem) clamp(1rem, 5vw, 2rem)",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>

          {/* Page title */}
          <div style={{ marginBottom: "clamp(1.5rem, 4vw, 2.5rem)" }}>
            <p style={{ margin: "0 0 .4rem", fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: T.rose }}>
              GlossyLuxHair
            </p>
            <h1 style={{ margin: "0 0 .5rem", fontFamily: T.ffDisplay, fontSize: "clamp(2rem, 5vw, 2.75rem)", fontWeight: 700, color: T.ink, letterSpacing: "-0.02em" }}>
              Customer Reviews
            </h1>
            <p style={{ margin: 0, fontSize: 15, color: T.warm400, lineHeight: 1.6 }}>
              Share your experience or read what others are saying.
            </p>
          </div>

          {/* ── Form card ── */}
          <div style={{
            background: "#fff",
            borderRadius: 16,
            border: `1px solid ${T.warm100}`,
            boxShadow: `0 2px 8px rgba(26,21,16,.04), 0 12px 40px rgba(192,80,110,.06)`,
            overflow: "hidden",
            marginBottom: "2.5rem",
            animation: "fadeUp 420ms cubic-bezier(0.16,1,0.3,1) both",
          }}>
            <div style={{ height: 3, background: `linear-gradient(90deg, transparent 0%, ${T.roseMid} 15%, ${T.rose} 50%, ${T.roseMid} 85%, transparent 100%)` }} />
            <div style={{ padding: "clamp(1.5rem, 4vw, 2rem)" }}>

              <h2 style={{ margin: "0 0 1.5rem", fontFamily: T.ffDisplay, fontSize: "1.4rem", fontWeight: 700, color: T.roseDark }}>
                Leave a Review
              </h2>

              {/* Success alert */}
              {success && (
                <div style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "0.9rem 1rem", borderRadius: 10,
                  border: "1px solid #b7e3c8", background: "#f0faf5",
                  marginBottom: "1.25rem",
                }}>
                  <span style={{ color: "#15803d", fontSize: 15 }}>✓</span>
                  <p style={{ margin: 0, fontSize: 13, color: "#1a5c35", fontFamily: T.ffBody }}>
                    Thank you! Your review has been posted successfully.
                  </p>
                </div>
              )}

              {/* Error alert */}
              {error && (
                <div style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "0.9rem 1rem", borderRadius: 10,
                  border: `1px solid ${T.roseMid}`, background: T.roseSoft,
                  marginBottom: "1.25rem",
                }}>
                  <span style={{ color: T.rose, fontSize: 15 }}>⚠</span>
                  <p style={{ margin: 0, fontSize: 13, color: T.roseDark, fontFamily: T.ffBody }}>{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                {/* Name + Rating row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "1rem", alignItems: "start", marginBottom: "1rem" }}>
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: errors.name ? T.rose : T.warm400, marginBottom: 6, fontFamily: T.ffBody }}>
                      Your Name *
                    </label>
                    <input
                      className="rv-input"
                      type="text"
                      placeholder="Jane D."
                      value={form.name}
                      onChange={set("name")}
                      onBlur={blur("name")}
                      autoComplete="name"
                    />
                    {errors.name && <p style={{ margin: "5px 0 0", fontSize: 12, color: T.rose, fontFamily: T.ffBody }}>{errors.name}</p>}
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: T.warm400, marginBottom: 10, fontFamily: T.ffBody }}>
                      Rating
                    </label>
                    <StarRating
                      value={form.rating}
                      onChange={(n) => setForm((p) => ({ ...p, rating: n }))}
                      size={26}
                    />
                  </div>
                </div>

                {/* Review textarea */}
                <div style={{ marginBottom: "1.25rem" }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: errors.review ? T.rose : T.warm400, marginBottom: 6, fontFamily: T.ffBody }}>
                    Your Review *
                  </label>
                  <textarea
                    className="rv-input"
                    rows={4}
                    placeholder="Tell us what you loved about your purchase…"
                    value={form.review}
                    onChange={set("review")}
                    onBlur={blur("review")}
                    style={{ resize: "vertical", lineHeight: 1.6 }}
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                    {errors.review
                      ? <p style={{ margin: 0, fontSize: 12, color: T.rose, fontFamily: T.ffBody }}>{errors.review}</p>
                      : <span />
                    }
                    <p style={{ margin: 0, fontSize: 11, color: form.review.length >= 10 ? T.warm400 : T.roseLight, fontFamily: T.ffBody }}>
                      {form.review.length} / 10 min
                    </p>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="rv-submit"
                  style={{
                    padding: "0.85rem 2rem",
                    fontFamily: T.ffBody,
                    fontWeight: 600,
                    fontSize: 14,
                    letterSpacing: "0.04em",
                    color: "#fff",
                    background: canSubmit ? T.rose : T.warm200,
                    border: "none",
                    borderRadius: 12,
                    cursor: canSubmit ? "pointer" : "not-allowed",
                    boxShadow: canSubmit ? `0 4px 20px rgba(192,80,110,.22)` : "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {loading ? (
                    <>
                      <span style={{
                        width: 14, height: 14,
                        border: "2px solid rgba(255,255,255,.4)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        display: "inline-block",
                        animation: "spin .7s linear infinite",
                      }} />
                      Posting…
                    </>
                  ) : (
                    <><span>✦</span> Post Review</>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* ── Reviews list ── */}
          {loadingReviews ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "3rem 0" }}>
              <div style={{ width: 32, height: 32, border: `3px solid ${T.roseMid}`, borderTopColor: T.rose, borderRadius: "50%", animation: "spin .8s linear infinite" }} />
            </div>
          ) : reviews.length > 0 ? (
            <>
              <h2 style={{ fontFamily: T.ffDisplay, fontSize: "clamp(1.5rem, 4vw, 2rem)", fontWeight: 700, color: T.ink, margin: "0 0 .25rem" }}>
                What our customers say
              </h2>
              <p style={{ margin: "0 0 1.5rem", fontSize: 13, color: T.warm400 }}>
                {reviews.length} verified review{reviews.length !== 1 ? "s" : ""}
              </p>
              <div className="rv-reviews-grid">
                {reviews.map((r, i) => (
                  <ReviewCard key={r.id} review={r} idx={i} />
                ))}
              </div>
            </>
          ) : (
            <div style={{ textAlign: "center", padding: "3rem 0", color: T.warm400 }}>
              <div style={{ fontSize: 40, marginBottom: "1rem" }}>💬</div>
              <p style={{ fontFamily: T.ffDisplay, fontSize: "1.2rem", margin: "0 0 .5rem", color: T.warm700 }}>No reviews yet</p>
              <p style={{ fontSize: 13, margin: 0 }}>Be the first to share your experience!</p>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default ReviewForm;